import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Alert, Table, Placeholder } from 'react-bootstrap';
import Button from 'components/base/Button';
import CarManagmentProvider, {
  useCarManagmentContext,
  Car,
  CarEvent
} from 'providers/CarManagmentProvider';
import {
  CarInfoCard,
  MaintenanceTable,
  AddMaintenanceModal,
  EditMaintenanceModal,
  DeleteConfirmationModal,
  ConfirmationModal
} from './components';

const index = () => {
  return (
    <CarManagmentProvider>
      <CarDetails />
    </CarManagmentProvider>
  );
};

const CarDetails = () => {
  const { guidCarro } = useParams<{ guidCarro: string }>();
  const {
    carsEvents,
    fetchCarMaintenanceEvents,
    carsMaintenance,
    createMaintenance,
    updateMaintenance,
    deleteMaintenance
  } = useCarManagmentContext();
  const [car, setCar] = useState<Car | null>(null);
  const [events, setEvents] = useState<CarEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<
    'state' | 'startDate' | 'endDate' | null
  >('startDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const navigate = useNavigate();

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>('');
  const [redirectTripIds, setRedirectTripIds] = useState<number[]>([]);
  const [conflictDetails, setConflictDetails] = useState<
    Array<{
      tripId: number;
      groupId: string;
      startDate: string;
      endDate: string;
    }>
  >([]);
  const [pendingAddEvent, setPendingAddEvent] = useState<{
    stateId: number;
    startDate: Date;
    endDate: Date | null;
    observation: string;
    local: string;
  } | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CarEvent | null>(null);
  // const [observationForIntervention, setObservationForIntervention] =
  //   useState<CarEvent | null>(null); // NEW

  // Read-only stateIds - only stateId 2 (Observação) is read-only
  const READ_ONLY_STATE_IDS = [2];

  // Sort handler
  const handleSort = (field: 'state' | 'startDate' | 'endDate') => {
    if (sortBy === field) {
      if (sortOrder === null) {
        setSortOrder('asc');
      } else if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        setSortOrder(null);
        setSortBy(null);
      }
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Load car and events
  useEffect(() => {
    setError(null);
    setCar(null);
    setEvents([]);

    if (!guidCarro) {
      setError('Nenhum carro foi encontrado. Por favor, tente novamente.');
      return;
    }

    if (carsMaintenance.length < 1) {
      setError('Aguarde, os dados estão a ser carregados...');
      return;
    }

    const selectedCar =
      carsMaintenance.find(c => c.guidCarro === guidCarro) || null;
    setCar(selectedCar);

    if (!selectedCar) {
      setError('O veículo não foi encontrado.');
    } else {
      const filteredEvents = carsEvents
        .filter(event => event.idcarro === selectedCar.id)
        .map(event => ({
          id: event.id,
          idcarro: event.idcarro,
          stateId: event.stateId,
          state: event.state,
          startDate: event.startDate || '',
          endDate: event.endDate || '',
          observation: event.observation || '',
          local: event.local || '',
          isRead: event.isRead || 0,
          isResolved: event.isResolved || 0,
          createdBy:
            event.createdBy ||
            event.createdByName ||
            event.userName ||
            event.username ||
            event.name ||
            ''
        }))
        .sort((a, b) => {
          const aHasEnd = Boolean(a.endDate);
          const bHasEnd = Boolean(b.endDate);
          if (aHasEnd !== bHasEnd) {
            return aHasEnd ? 1 : -1;
          }

          const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
          return bStart - aStart;
        });

      setEvents(filteredEvents);
    }
  }, [guidCarro, carsMaintenance, carsEvents]);

  useEffect(() => {
    if (car) {
      fetchCarMaintenanceEvents(car.id);
    }
  }, [car]);

  // Open edit modal with pre-filled data
  const openEditModal = (event: CarEvent) => {
    setSelectedEvent(event);
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (event: CarEvent) => {
    setSelectedEvent(event);
    setShowDeleteModal(true);
  };

  // Add Event
  const handleAddEvent = async (event: {
    stateId: number;
    startDate: Date;
    endDate: Date | null;
    observation: string;
    local: string;
  }) => {
    if (!car) return;

    const result = await createMaintenance({
      idcarro: car.id,
      stateId: event.stateId,
      startDate: event.startDate,
      endDate: event.endDate || null,
      observation: event.observation || '',
      isActive: 1,
      local: event.local || ''
    });

    if (result.success) {
      setShowAddModal(false);
      setErrorModalMessage('');
      setShowErrorModal(false);
      setPendingAddEvent(null);
      setConflictDetails([]);
      return;
    }

    if (result.error === 'conflict' && result.conflictDetails?.length) {
      setPendingAddEvent(event);
      setConflictDetails(result.conflictDetails);
      setShowAddModal(false);
      setShowConflictModal(true);
      return;
    }

    setErrorModalMessage(
      result.error || 'Falha ao adicionar evento de intervenção'
    );
    setShowErrorModal(true);
  };

  const handleConfirmAddEvent = async () => {
    if (!car || !pendingAddEvent) {
      setShowConflictModal(false);
      setPendingAddEvent(null);
      return;
    }

    const result = await createMaintenance(
      {
        idcarro: car.id,
        stateId: pendingAddEvent.stateId,
        startDate: pendingAddEvent.startDate,
        endDate: pendingAddEvent.endDate || null,
        observation: pendingAddEvent.observation || '',
        isActive: 1,
        local: pendingAddEvent.local || ''
      },
      true
    );

    if (result.success) {
      setShowConflictModal(false);
      setPendingAddEvent(null);
      setConflictDetails([]);

      if (result.revokedTripIds?.length) {
        setRedirectTripIds(result.revokedTripIds);
        setShowRedirectModal(true);
      }
      return;
    }

    setShowConflictModal(false);
    setErrorModalMessage(
      result.error || 'Falha ao adicionar evento de intervenção'
    );
    setShowErrorModal(true);
  };

  const handleRedirectToAllocation = () => {
    setShowRedirectModal(false);
    setRedirectTripIds([]);
    navigate('/dashboard/carManagement/alocationTable', {
      state: {
        carId: car?.id,
        revokedTripIds: redirectTripIds
      }
    });
  };

  const handleSkipRedirect = () => {
    setShowRedirectModal(false);
    setRedirectTripIds([]);
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorModalMessage('');
  };

  // Edit Event
  const handleUpdateEvent = async (event: {
    id: number;
    idcarro: number;
    stateId: number;
    startDate: Date;
    endDate: Date | null;
    observation: string;
    local: string;
    isRead: number;
    isResolved: number;
  }) => {
    if (!car) return;

    const result = await updateMaintenance({
      id: event.id,
      idcarro: event.idcarro,
      stateId: event.stateId,
      startDate: event.startDate,
      endDate: event.endDate || null,
      observation: event.observation || '',
      local: event.local || '',
      isRead: event.isRead,
      isResolved: event.isResolved
    });

    if (result.success) {
      setShowEditModal(false);
      setSelectedEvent(null);
    } else {
      alert(result.error || 'Falha ao atualizar evento de intervenção');
    }
  };

  // Delete Event
  const handleDeleteEvent = async (event: CarEvent) => {
    if (!selectedEvent || !car) return;

    const result = await deleteMaintenance({
      id: event.id,
      idcarro: car.id
    });

    if (result.success) {
      setShowDeleteModal(false);
      setSelectedEvent(null);
    } else {
      alert(result.error || 'Falha ao eliminar evento de intervenção');
    }
  };

  // Mark observation as resolved
  const handleMarkObservationAsResolved = async (event: CarEvent) => {
    if (!car) return;

    try {
      // Call backend to mark as read

      // Assuming backend will update and refresh events
      const result = await updateMaintenance({
        id: event.id,
        idcarro: car.id,
        stateId: event.stateId,
        startDate: new Date(event.startDate),
        endDate: event.endDate ? new Date(event.endDate) : null,
        observation: event.observation,
        local: event.local,
        isRead: event.isRead,
        isResolved: 1
      });

      if (!result.success) {
        setErrorModalMessage(
          result.error || 'Falha ao marcar observação como tratada'
        );
        setShowErrorModal(true);
      }
      // Refresh events to get updated isResolved status
      console.log('HERE 1');
      await fetchCarMaintenanceEvents(car.id);
    } catch (error) {
      setErrorModalMessage('Falha ao marcar observação como tratada');
      setShowErrorModal(true);
    }
  };

  // NEW: Create intervention from observation (marks observation as resolved) (observation: CarEvent)
  const handleCreateInterventionFromObservation = () => {
    // setObservationForIntervention(observation);
    setShowAddModal(true);
  };

  // Loading state
  if (carsMaintenance.length < 1) {
    return (
      <div className="car-details-container">
        <div className="car-details-header">
          <h2 className="car-details-title">Detalhes do Veículo</h2>
          <Button
            variant="outline-secondary"
            onClick={() =>
              navigate('/dashboard/carManagement/carStateManagement')
            }
            disabled
          >
            Voltar
          </Button>
        </div>
        <Row>
          <Col md={3} className="d-flex flex-column">
            <Card className="car-details-card">
              <Card.Body className="car-details-card-body">
                {[...Array(4)].map((_, index) => (
                  <Row key={index} className="car-details-row">
                    <Col md={12}>
                      <Placeholder
                        animation="glow"
                        xs={4}
                        className="w-50 mb-1"
                      />
                      <Placeholder animation="glow" xs={8} className="w-100" />
                    </Col>
                  </Row>
                ))}
              </Card.Body>
            </Card>
            <div className="add-button-container">
              <Placeholder animation="glow" xs={4} className="w-100" />
            </div>
          </Col>
          <Col md={9} className="d-flex flex-column">
            <Card className="table-card">
              <Card.Body className="table-card-body">
                <div className="table-container">
                  <Table size="sm" className="car-details-table">
                    <thead className="table-header">
                      <tr className="table-row">
                        <th className="table-cell sortable-header">
                          <Placeholder animation="glow" xs={3} />
                        </th>
                        <th className="table-cell table-cell-start-date sortable-header">
                          <Placeholder animation="glow" xs={3} />
                        </th>
                        <th className="table-cell table-cell-end-date sortable-header">
                          <Placeholder animation="glow" xs={3} />
                        </th>
                        <th className="table-cell table-cell-observation">
                          <Placeholder animation="glow" xs={3} />
                        </th>
                        <th className="table-cell table-cell-action">
                          <Placeholder animation="glow" xs={2} />
                        </th>
                      </tr>
                    </thead>
                  </Table>
                  <div className="tbody-container">
                    <Table size="sm" className="car-details-table">
                      <tbody className="table-tbody">
                        {[...Array(4)].map((_, i) => (
                          <tr key={i} className="table-row">
                            <td className="table-cell table-cell-state">
                              <Placeholder animation="glow" xs={8} />
                            </td>
                            <td className="table-cell table-cell-start-date">
                              <Placeholder animation="glow" xs={6} />
                            </td>
                            <td className="table-cell table-cell-end-date">
                              <Placeholder animation="glow" xs={6} />
                            </td>
                            <td className="table-cell table-cell-observation">
                              <Placeholder animation="glow" xs={10} />
                            </td>
                            <td className="table-cell table-cell-action">
                              <Placeholder animation="glow" xs={4} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Error state
  if (error && !car) {
    return (
      <div className="car-details-container">
        <div className="car-details-header">
          <h2 className="car-details-title">Detalhes do Veículo</h2>
          <Button
            variant="outline-secondary"
            onClick={() =>
              navigate('/dashboard/carManagement/carStateManagement')
            }
          >
            Voltar
          </Button>
        </div>
        <Alert variant="warning" className="py-3">
          {error}
        </Alert>
      </div>
    );
  }

  const groupedConflictDetails = conflictDetails.filter(
    (detail, index, details) =>
      details.findIndex(item => item.groupId === detail.groupId) === index
  );
  const conflictTripCount = groupedConflictDetails.length;
  const conflictTripLabel = conflictTripCount === 1 ? 'viagem' : 'viagens';
  const conflictTripArticle = conflictTripCount === 1 ? 'esta' : 'estas';
  const conflictTripStatus = conflictTripCount === 1 ? 'agendada' : 'agendadas';
  const conflictTripAction =
    conflictTripCount === 1 ? 'viagem pendente' : 'viagens pendentes';

  // Success state
  return (
    <div className="car-details-container">
      <AddMaintenanceModal
        show={showAddModal}
        car={car}
        onHide={() => {
          setShowAddModal(false);
          // setObservationForIntervention(null);
        }}
        onAdd={handleAddEvent}
      />

      <ConfirmationModal
        show={showConflictModal}
        title="Conflito de Viagens"
        message={`O carro já tem ${conflictTripCount} ${conflictTripLabel} ${conflictTripStatus} durante este período. Deseja continuar e tornar ${conflictTripArticle} ${conflictTripAction}?`}
        details={groupedConflictDetails.map(item => ({
          label: `Viagem ${item.groupId}`,
          value: `Início: ${new Date(item.startDate).toLocaleString('pt-PT')} — Fim: ${item.endDate !== 'N/A' ? new Date(item.endDate).toLocaleString('pt-PT') : 'N/A'}`
        }))}
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={handleConfirmAddEvent}
        onCancel={() => {
          setShowConflictModal(false);
          setPendingAddEvent(null);
          setConflictDetails([]);
        }}
      />

      <ConfirmationModal
        show={showRedirectModal}
        title="Redirecionar para alocação?"
        message="As viagens afetadas foram tornadas pendentes. Deseja atribuir outro carro agora?"
        confirmText="Sim, ir para alocação"
        cancelText="Não"
        onConfirm={handleRedirectToAllocation}
        onCancel={handleSkipRedirect}
      />

      <ConfirmationModal
        show={showErrorModal}
        title="Erro ao adicionar intervenção"
        message={errorModalMessage}
        hideCancel
        confirmText="Fechar"
        onConfirm={handleCloseErrorModal}
        onCancel={handleCloseErrorModal}
      />

      <EditMaintenanceModal
        show={showEditModal}
        car={car}
        event={selectedEvent}
        readOnly={
          selectedEvent
            ? READ_ONLY_STATE_IDS.includes(selectedEvent.stateId)
            : false
        }
        onHide={() => {
          setShowEditModal(false);
          setSelectedEvent(null);
        }}
        onEdit={handleUpdateEvent}
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        event={selectedEvent}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedEvent(null);
        }}
        onDelete={handleDeleteEvent}
      />

      <div className="car-details-header">
        <h2 className="car-details-title">Detalhes do Veículo</h2>
        <Button
          variant="outline-secondary"
          onClick={() =>
            navigate('/dashboard/carManagement/carStateManagement')
          }
        >
          Voltar
        </Button>
      </div>

      <Row>
        <Col md={3} className="d-flex flex-column">
          <CarInfoCard car={car} />
          <div className="add-button-container">
            <Button
              variant="outline-primary"
              className="add-button"
              onClick={() => setShowAddModal(true)}
            >
              Adicionar Intervenção
            </Button>
          </div>
        </Col>

        <Col md={9} className="d-flex flex-column">
          <MaintenanceTable
            events={events}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
            onMarkAsResolved={handleMarkObservationAsResolved}
            onCreateIntervention={handleCreateInterventionFromObservation}
          />
        </Col>
      </Row>
    </div>
  );
};

export default index;
